import { useQuery } from '@tanstack/react-query'
import type { Role } from '@kronos/domain'
import { repository } from '../../data'
import type { WorkRow } from '../../data/repository'
import { BuildOrderCard } from '../BuildOrderCard'
import { Icon } from '../icons'
import { Badge, EmptyState, PageHeader, Panel, Section } from '../primitives'
import { Link } from '../shell/Link'

/**
 * Work — a technician's jobs: what is under way (yours first), and the builds
 * on the floor that can be picked up. Scanning a part is a button here rather
 * than a tab of its own, because it is how a job starts, not a job.
 */
export function WorkView({ meId, roles }: { meId: string; roles: readonly Role[] }): JSX.Element {
  const work = useQuery({ queryKey: ['work', false], queryFn: () => repository.work(false) })
  const orders = useQuery({ queryKey: ['build-orders'], queryFn: () => repository.buildOrders() })

  const rows = work.data ?? []
  const open = [...rows.filter((r) => r.performedBy === meId), ...rows.filter((r) => r.performedBy !== meId)]
  const floor = (orders.data ?? []).filter((o) => o.state === 'released')

  return (
    <div className="page page--narrow">
      <PageHeader
        title="Work"
        subtitle="Jobs under way, and builds you can pick up."
        actions={
          <Link to={{ name: 'scan', unknown: null }} className="button primary">
            <Icon name="scan" size={18} />
            Scan a part
          </Link>
        }
      />

      <Section label={`Under way · ${open.length}`}>
        <Panel>
          {work.isPending ? <p className="muted" style={{ padding: 16, margin: 0 }}>Loading…</p> : null}
          {work.isSuccess && open.length === 0 ? (
            <EmptyState icon="work" title="Nothing under way">
              Start a build below, or scan a part to start a job on it.
            </EmptyState>
          ) : (
            <div className="row-list">
              {open.map((r) => (
                <Job key={r.id} row={r} mine={r.performedBy === meId} />
              ))}
            </div>
          )}
        </Panel>
      </Section>

      <Section label={`Builds on the floor · ${floor.length}`}>
        {orders.isSuccess && floor.length === 0 ? (
          <Panel>
            <EmptyState icon="builds" title="No builds released">
              When an engineer releases a build, it shows up here to pick up.
            </EmptyState>
          </Panel>
        ) : null}
        {floor.map((o) => (
          <BuildOrderCard key={o.id} order={o} roles={roles} />
        ))}
      </Section>
    </div>
  )
}

function Job({ row, mine }: { row: WorkRow; mine: boolean }): JSX.Element {
  return (
    <Link to={{ name: 'execution', id: row.id }} className="row-item">
      <span className="row-item__main">
        <span className="row-item__title">{row.procedureName}</span>
        <span className="row-item__meta">
          {row.fleetNumber ?? row.typeName} · <span className="serial">{row.serial}</span>
        </span>
      </span>
      <span className="row-item__side">
        {mine ? <Badge tone="info">Yours</Badge> : null}
        <span className="figure">
          {row.answered} of {row.steps}
        </span>
      </span>
    </Link>
  )
}
